import { useMutation } from '@apollo/client/react';
import {
  ENDPUNKTE_SPEICHERN,
  ZUGANG_BEARBEITEN,
} from '../lib/graphql/mutations.ts';
import * as Sentry from '@sentry/react';
import type { EndpunktDefinitionInput } from '../gql/graphql.ts';

export type BearbeitenErgebnis =
  | 'erfolg'
  | 'endpunkteFehlgeschlagen'
  | 'titelZuLang'
  | 'nichtEditierbar'
  | 'fehlgeschlagen';

export type ZugangBearbeiten = {
  bearbeiten: (
    uidZugang: string,
    titel: string,
    definitionen: EndpunktDefinitionInput[],
  ) => Promise<BearbeitenErgebnis>;
  loading: boolean;
};

export function useZugangBearbeiten(): ZugangBearbeiten {
  const [zugangBearbeiten, { loading: zugangBearbeitenLaeuft }] =
    useMutation(ZUGANG_BEARBEITEN);

  const [endpunkteSpeichern, { loading: endpunkteSpeichernLaeuft }] =
    useMutation(ENDPUNKTE_SPEICHERN);

  async function bearbeiten(
    uidZugang: string,
    titel: string,
    definitionen: EndpunktDefinitionInput[],
  ): Promise<BearbeitenErgebnis> {
    try {
      const { data } = await zugangBearbeiten({
        variables: { input: { uidZugang, beschreibung: titel } },
      });

      const ergebnis = data?.bearbeiteZugang;

      if (ergebnis?.__typename === 'ZugangBearbeitenBeschreibungTooLong') {
        return 'titelZuLang';
      }
      if (ergebnis?.__typename === 'ZugangBearbeitenZugangNotEditable') {
        return 'nichtEditierbar';
      }
      if (ergebnis?.__typename !== 'ZugangBearbeitenSuccess') {
        return 'fehlgeschlagen';
      }
    } catch (error) {
      Sentry.captureException(error);
      return 'fehlgeschlagen';
    }

    try {
      const { data } = await endpunkteSpeichern({
        variables: { zugangUid: uidZugang, endpunktDefinitionen: definitionen },
        update(cache, { data }) {
          if (
            data?.saveEndpunktDefinitionen.__typename !==
            'SaveEndpunktDefinitionSuccess'
          ) {
            return;
          }
          cache.modify({
            id: cache.identify({ __typename: 'Zugang', uid: uidZugang }),
            fields: {
              endpunktDefinitionen: () =>
                definitionen.map((definition) => ({
                  __typename: 'EndpunktDefinition',
                  ...definition,
                })),
            },
          });
        },
      });

      if (
        data?.saveEndpunktDefinitionen.__typename ===
        'SaveEndpunktDefinitionZugangNotEditable'
      ) {
        return 'nichtEditierbar';
      }
      if (
        data?.saveEndpunktDefinitionen.__typename !==
        'SaveEndpunktDefinitionSuccess'
      ) {
        return 'endpunkteFehlgeschlagen';
      }
    } catch (error) {
      Sentry.captureException(error);
      return 'endpunkteFehlgeschlagen';
    }

    return 'erfolg';
  }

  return {
    bearbeiten,
    loading: zugangBearbeitenLaeuft || endpunkteSpeichernLaeuft,
  };
}
